import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const AwardsRecognitionSection = () => {
  const [isVisible, setIsVisible] = useState(true); // Changed to true for mobile compatibility
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Lowered threshold for better mobile detection
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const awards = [
    {
      id: 1,
      title: "Best Customer Service Platform",
      organization: "Indian Gaming Association",
      year: "2024",
      description: "Recognized for exceptional WhatsApp-based customer support and personalized player assistance.",
      icon: "Award",
      image: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: 2,
      title: "Most Trusted Gaming Platform",
      organization: "Digital India Gaming Awards",
      year: "2023",
      description: "Awarded for maintaining highest standards of security, transparency, and player trust.",
      icon: "Shield",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: 3,
      title: "Innovation in Mobile Gaming",
      organization: "Tech Excellence Awards",
      year: "2023",
      description: "Recognized for pioneering WhatsApp integration in online gaming platforms.",
      icon: "Smartphone",
      image: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=300&h=200&fit=crop&crop=center"
    },
    {
      id: 4,
      title: "Excellence in Payment Security",
      organization: "Fintech Security Council",
      year: "2022",
      description: "Honored for implementing advanced security measures and instant payout systems.",
      icon: "Lock",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=200&fit=crop&crop=center"
    }
  ];

  const recognitions = [
    {
      id: 1,
      title: "Featured in Economic Times",
      description: "Nitin\'s innovative approach to customer service through WhatsApp highlighted as industry game-changer.",
      date: "March 2024",
      type: "Media Coverage",
      icon: "Newspaper"
    },
    {
      id: 2,
      title: "Gaming Industry Leadership",
      description: "CEO Rajesh Kumar recognized as one of India\'s top gaming industry leaders by Business Today.",
      date: "January 2024",
      type: "Leadership Recognition",
      icon: "Crown"
    },
    {
      id: 3,
      title: "Customer Satisfaction Excellence",
      description: "Achieved 99.2% customer satisfaction rating in independent survey by Gaming Research Institute.",
      date: "December 2023",
      type: "Customer Rating",
      icon: "Star"
    },
    {
      id: 4,
      title: "Technology Innovation Award",
      description: "Recognized by Indian Software Association for breakthrough in gaming platform technology.",
      date: "October 2023",
      type: "Technology Award",
      icon: "Zap"
    }
  ];

  const certifications = [
    {
      name: "ISO 27001",
      description: "Information Security Management",
      validUntil: "2025",
      icon: "Shield"
    },
    {
      name: "PCI DSS",
      description: "Payment Card Industry Compliance",
      validUntil: "2024",
      icon: "CreditCard"
    },
    {
      name: "SOC 2 Type II",
      description: "Security & Availability Controls",
      validUntil: "2025",
      icon: "Lock"
    },
    {
      name: "Gaming License",
      description: "Sikkim Gaming Commission",
      validUntil: "2025",
      icon: "Award"
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="Trophy" size={32} color="white" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-6">
            Awards & <span className="text-gradient-royal">Recognition</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our commitment to excellence has been recognized by leading industry organizations and publications across India.
          </p>
        </div>

        {/* Awards Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {awards?.map((award, index) => (
            <div
              key={award?.id}
              className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="bg-white rounded-2xl shadow-subtle border border-gray-100 overflow-hidden hover:shadow-royal transition-all duration-300 hover-lift">
                {/* Award Image */}
                <div className="relative h-48">
                  <Image
                    src={award?.image}
                    alt={award?.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                        <Icon name={award?.icon} size={24} color="white" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{award?.year}</p>
                        <p className="text-gray-200 text-sm">{award?.organization}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Award Info */}
                <div className="p-6">
                  <h3 className="text-xl font-heading font-bold text-gray-900 mb-3">{award?.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{award?.description}</p>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-bold rounded-full">
                      {award?.organization}
                    </span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} color="var(--color-gray-400)" />
                      <span className="text-xs text-gray-400">{award?.year}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recognition Timeline */}
        <div className={`mb-20 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Industry <span className="text-gradient-royal">Recognition</span>
          </h3>
          
          <div className="space-y-6">
            {recognitions?.map((recognition, index) => (
              <div
                key={recognition?.id}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                style={{ transitionDelay: `${(index + 4) * 150}ms` }}
              >
                <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={recognition?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900">{recognition?.title}</h4>
                        <span className="px-3 py-1 bg-accent/20 text-accent text-xs font-bold rounded-full">
                          {recognition?.type}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{recognition?.description}</p>
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} color="var(--color-gray-400)" />
                        <span className="text-xs text-gray-400">{recognition?.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className={`transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Industry <span className="text-gradient-royal">Certifications</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications?.map((cert, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 8) * 100}ms` }}
              >
                <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={cert?.icon} size={24} color="white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{cert?.name}</h4>
                  <p className="text-gray-600 text-sm mb-3">{cert?.description}</p>
                  <div className="flex items-center justify-center space-x-1">
                    <Icon name="Calendar" size={14} color="var(--color-success)" />
                    <span className="text-xs text-success font-medium">Valid until {cert?.validUntil}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className={`mt-16 transition-all duration-1000 delay-1200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Trophy" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">15+</p>
                <p className="text-gray-600 text-sm">Awards Won</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Star" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">4.9/5</p>
                <p className="text-gray-600 text-sm">Industry Rating</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">50K+</p>
                <p className="text-gray-600 text-sm">Satisfied Players</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Calendar" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">5+</p>
                <p className="text-gray-600 text-sm">Years Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AwardsRecognitionSection;